import { useAccount, useChainId, useConfig, useWatchContractEvent } from 'wagmi'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ZERO_ADDRESS } from '@/web3/constants'
import { ContractName, getContractAddress } from '@/constants/contract-addresses'
import { blockpotRewardTrackerAbi } from '@/abi/blockPotRewardTrackerAbi'
import { BlockPotRewardTrackerState } from '@/types/lottery'
import useBlockPotRewardTrackerRead from '../read/useBlockPotRewardTrackerRead'

export default function useBlockPotRewardTrackerState() {
    const chainId = useChainId()
    const contractAddress = getContractAddress(chainId, ContractName.BLOCK_POT_REWARD_TRACKER)
    const config = useConfig()
    const { address } = useAccount()
    const queryClient = useQueryClient()
    const blockPotRewardTracker = useBlockPotRewardTrackerRead()

    useWatchContractEvent({
        address: contractAddress,
        abi: blockpotRewardTrackerAbi,
        eventName: 'Transfer',
        onLogs: (logs) => {
            if (logs.some((l) => l.args.from === address || l.args.to === address)) {
                queryClient.invalidateQueries({ queryKey: ['blockPotRewardTracker']})
            }
        }
    })

    useWatchContractEvent({
        address: contractAddress,
        abi: blockpotRewardTrackerAbi,
        eventName: 'Claim',
        onLogs: (logs) => {
            if (logs.some((l) => l.args.receiver === address)) {
                queryClient.invalidateQueries({ queryKey: ['blockPotRewardTracker']})
            }
        }
    })

    const { data } = useQuery<BlockPotRewardTrackerState>({
        queryKey: ['blockPotRewardTracker', chainId, address ?? ZERO_ADDRESS],
        queryFn: async () => {
            const stakedBalance = await blockPotRewardTracker.stakedAmount([address ?? ZERO_ADDRESS])
            const claimableReward = await blockPotRewardTracker.claimable([address ?? ZERO_ADDRESS])
            return {
                stakedBalance,
                claimableReward
            }
        }
    })

    return data ?? {
        stakedBalance: 0n,
        claimableReward: 0n,
    }
}